import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { gbp } from "@/lib/invoice";

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function InvoiceEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (isEdit) {
        const { data } = await api.get(`/admin/invoices/${id}`);
        if (!ignore) setForm(data);
      } else {
        const settings = (await api.get("/admin/invoice-settings")).data || {};
        if (ignore) return;
        setForm({
          customer_name: "", customer_phone: "", customer_email: "", customer_address: "", customer_postcode: "",
          line_items: [{ description: "", quantity: 1, unit_price: 0 }],
          vat_enabled: settings.default_vat_enabled || false,
          vat_rate: settings.default_vat_rate || 20,
          issue_date: todayIso(), due_date: addDays(settings.payment_terms_days || 14),
          notes: "", status: "draft",
        });
      }
    })();
    return () => { ignore = true; };
  }, [id, isEdit]);

  if (!form) return <p className="text-slate-500">Loading…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => (idx === i ? { ...li, [k]: v } : li)) }));
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const vat = form.vat_enabled ? subtotal * (Number(form.vat_rate) || 0) / 100 : 0;
  const total = subtotal + vat;

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    const payload = {
      lead_id: form.lead_id || null,
      customer_name: form.customer_name, customer_phone: form.customer_phone, customer_email: form.customer_email,
      customer_address: form.customer_address, customer_postcode: form.customer_postcode,
      line_items: form.line_items.map((li) => ({ description: li.description, quantity: Number(li.quantity) || 0, unit_price: Number(li.unit_price) || 0 })),
      vat_enabled: form.vat_enabled, vat_rate: Number(form.vat_rate) || 0,
      issue_date: form.issue_date, due_date: form.due_date, notes: form.notes,
      status: form.status === "overdue" ? "sent" : form.status,
    };
    try {
      let res;
      if (isEdit) res = await api.put(`/admin/invoices/${id}`, payload);
      else res = await api.post("/admin/invoices", payload);
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices-summary"] });
      qc.invalidateQueries({ queryKey: ["invoice", res.data.id] });
      toast.success(isEdit ? "Invoice updated" : "Invoice created");
      navigate(`/admin/invoices/${res.data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-3 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";

  return (
    <div data-testid="invoice-editor-page">
      <Link to={isEdit ? `/admin/invoices/${id}` : "/admin/invoices"} className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-semibold"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl mt-3">{isEdit ? `Edit ${form.invoice_number}` : "New invoice"}</h1>

      {/* Customer */}
      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <h2 className="font-display font-bold text-slate-900 mb-4">Customer</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Name *</label><input className={inputCls} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="invoice-customer-name" /></div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone</label><input className={inputCls} value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="invoice-customer-phone" /></div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label><input className={inputCls} value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="invoice-customer-email" /></div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Postcode</label><input className={inputCls} value={form.customer_postcode} onChange={(e) => set("customer_postcode", e.target.value)} data-testid="invoice-customer-postcode" /></div>
          <div className="sm:col-span-2"><label className="block text-sm font-semibold text-slate-700 mb-1.5">Address</label><input className={inputCls} value={form.customer_address} onChange={(e) => set("customer_address", e.target.value)} data-testid="invoice-customer-address" /></div>
        </div>
      </div>

      {/* Line items */}
      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <h2 className="font-display font-bold text-slate-900 mb-4">Line items</h2>
        <div className="space-y-3">
          {form.line_items.map((li, i) => (
            <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2 items-start" data-testid={`invoice-line-${i}`}>
              <input className={inputCls.replace("w-full", "w-full sm:w-auto sm:flex-1 min-w-0")} placeholder="Description" value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} />
              <input type="number" step="0.01" className={inputCls.replace("w-full", "w-20 shrink-0")} placeholder="Qty" value={li.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`invoice-line-qty-${i}`} />
              <input type="number" step="0.01" className={inputCls.replace("w-full", "min-w-0 flex-1 sm:flex-none sm:w-28")} placeholder="Unit £" value={li.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`invoice-line-price-${i}`} />
              <div className="w-20 sm:w-24 shrink-0 text-right py-2.5 font-semibold text-slate-700 text-sm">{gbp((Number(li.quantity) || 0) * (Number(li.unit_price) || 0))}</div>
              <button onClick={() => removeItem(i)} disabled={form.line_items.length === 1} className="h-10 w-10 shrink-0 rounded-lg bg-red-50 grid place-items-center hover:bg-red-100 disabled:opacity-40" data-testid={`invoice-line-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
            </div>
          ))}
        </div>
        <button onClick={addItem} className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-green-700 hover:text-green-800" data-testid="invoice-add-line"><Plus className="h-4 w-4" /> Add line</button>

        <div className="mt-5 border-t border-slate-100 pt-4 space-y-2 max-w-xs ml-auto">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{gbp(subtotal)}</span></div>
          <div className="flex items-center justify-between text-sm gap-3">
            <label className="flex items-center gap-2 text-slate-500">
              <input type="checkbox" checked={form.vat_enabled} onChange={(e) => set("vat_enabled", e.target.checked)} className="h-4 w-4 accent-green-600" data-testid="invoice-vat-toggle" />
              VAT
              {form.vat_enabled && <input type="number" className="w-16 rounded border border-slate-300 px-2 py-1 text-xs" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} data-testid="invoice-vat-rate" />}
              {form.vat_enabled && "%"}
            </label>
            <span className="font-semibold">{gbp(vat)}</span>
          </div>
          <div className="flex justify-between text-base border-t border-slate-100 pt-2"><span className="font-bold text-slate-900">Total</span><span className="font-display font-black text-slate-900">{gbp(total)}</span></div>
        </div>
      </div>

      {/* Meta */}
      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm grid sm:grid-cols-2 gap-4">
        <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Issue date</label><input type="date" className={inputCls} value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} data-testid="invoice-issue-date" /></div>
        <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Due date</label><input type="date" className={inputCls} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-date" /></div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Status</label>
          <select className={inputCls} value={form.status === "overdue" ? "sent" : form.status} onChange={(e) => set("status", e.target.value)} data-testid="invoice-status-select">
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
          </select>
        </div>
        <div className="sm:col-span-2"><label className="block text-sm font-semibold text-slate-700 mb-1.5">Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="invoice-notes" /></div>
      </div>

      <div className="mt-5 flex gap-3">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60" data-testid="invoice-save">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save invoice"}
        </button>
        <button onClick={() => navigate(isEdit ? `/admin/invoices/${id}` : "/admin/invoices")} className="rounded-xl border border-slate-300 px-6 py-3 font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
}
